import React from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';

/**
 * Thin wrapper around the official hCaptcha React component.
 *
 * Site keys:
 *   - dev (`import.meta.env.DEV` true): hCaptcha's always-pass test key so local
 *     development never blocks on a real challenge.
 *   - prod: `VITE_HCAPTCHA_SITE_KEY` from the environment.
 *
 * Behavior:
 *   - On successful challenge, calls `onVerify(token)` with the hCaptcha token.
 *   - On expiry/error, calls `onVerify('')` so the caller can re-disable submit.
 *   - Exposes a `resetRef` so callers can imperatively reset the captcha after a
 *     failed server-side verification (e.g. when `verifyCaptcha` returns
 *     `success: false`).
 */

// hCaptcha's documented always-pass test key — safe to commit; only valid in dev.
export const HCAPTCHA_TEST_SITE_KEY = '10000000-ffff-ffff-ffff-000000000001';

/**
 * Resolves the hCaptcha site key for the current environment.
 *
 * - In dev (`isDev === true`): always returns the documented always-pass test
 *   key so local development isn't gated on a real challenge.
 * - In prod: returns the configured `VITE_HCAPTCHA_SITE_KEY` if set.
 * - Returns an empty string (fail-closed) when prod is configured without the
 *   env var so the component can refuse to render the widget and surface a
 *   loud config-error UI instead of silently allowing the form through.
 *
 * Exported so the resolution rule can be unit-tested in isolation (the JSX
 * around it is rendered in `vite` mode which we cannot exercise from vitest's
 * `node` environment without adding `jsdom` — banned for this pass).
 */
export const resolveSiteKey = (isDev: boolean, envKey?: string): string => {
  if (isDev) return HCAPTCHA_TEST_SITE_KEY;
  return envKey ?? '';
};

export type CaptchaFieldHandle = {
  reset: () => void;
};

interface CaptchaFieldProps {
  onVerify: (token: string) => void;
  className?: string;
}

export const CaptchaField = React.forwardRef<CaptchaFieldHandle, CaptchaFieldProps>(
  function CaptchaField({ onVerify, className }, ref) {
    const captchaRef = React.useRef<HCaptcha | null>(null);

    const siteKey = resolveSiteKey(
      import.meta.env.DEV,
      import.meta.env.VITE_HCAPTCHA_SITE_KEY as string | undefined
    );

    React.useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          captchaRef.current?.resetCaptcha();
          onVerify('');
        },
      }),
      [onVerify]
    );

    if (!siteKey) {
      // In prod, refuse to render an empty hCaptcha — it would silently allow the
      // form through with an empty token. Log loudly so Sentry (once wired) picks
      // this up as a config error.
      console.error(
        'CaptchaField: VITE_HCAPTCHA_SITE_KEY no está configurado. El captcha no se renderizará.'
      );
      return (
        <div
          role="alert"
          className={`bg-semantic-error-light border border-semantic-error text-semantic-error px-4 py-3 rounded-md text-body-sm ${className ?? ''}`}
        >
          Captcha no disponible. Contacta a soporte.
        </div>
      );
    }

    return (
      <div className={className}>
        <HCaptcha
          ref={captchaRef}
          sitekey={siteKey}
          onVerify={(token) => onVerify(token)}
          onExpire={() => onVerify('')}
          onError={() => onVerify('')}
        />
      </div>
    );
  }
);
